import { computed, onMounted, toValue, type MaybeRefOrGetter } from "vue";
import type { ListProps } from "../components/List/ListWidget.vue";
import type { SliderProps } from "../components/Slider/RecoSliderWidget.vue";

/**
 * Calculates whether the widget and title should be rendered based on the provided props and heading.
 * Also logs warnings to the console if the widget or title will not be rendered.
 * @param props The props of the widget (currently Slider or List)
 * @param finalHeading The final heading to be displayed in the widget (can be a ref or getter)
 * @param widgetName The name of the widget for logging purposes
 * @param isSponsored Indicates whether the widget represents a sponsored scenario.
 * @returns
 */
export const useShouldRender = (
  props: SliderProps | ListProps,
  finalHeading: MaybeRefOrGetter<string | null>,
  widgetName: string,
  isSponsored: MaybeRefOrGetter<boolean> = false,
) => {
  const shouldRenderWidget = computed(() => props.showTitle === false || toValue(finalHeading) || toValue(isSponsored));
  const shouldRenderTitle = computed(() => props.showTitle !== false && toValue(finalHeading));

  onMounted(() => {
    if (!shouldRenderWidget.value) {
      console.warn(
        `[Reco ${props.scenario} ${widgetName}]: 'showTitle' is not set to false but no valid 'heading' could be created.
      The slider will not be displayed.`,
      );
    }
    if (!shouldRenderTitle.value) {
      console.warn(
        `[Reco ${props.scenario} ${widgetName}]: 'showTitle' is set to false! The slider will be displayed without the title.`,
      );
    }
  });

  return {
    shouldRenderWidget,
    shouldRenderTitle,
  };
};
